import {
  Droplets,
  ChevronDown,
  Calendar,
  BarChart3,
  TrendingUp,
  Download,
  Search,
  X,
} from "lucide-react";
import { motion } from "framer-motion";
import { useTheme } from "../context/ThemeContext";
import { useState } from "react";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

import WaterUsageTab from "../components/WaterUsageTab";
import AggregateUsageTab from "../components/AggregateUsageTab";
import MetersByFlowTab from "../components/MetersByFlowTab";
import InfoTooltip from "../components/ui/InfoTooltip";

export function WaterUsageDetail({ onBack }) {
  const [activeTab, setActiveTab] = useState("waterUsage");
  const [viewType, setViewType] = useState("daily");
  const { colors } = useTheme();
  const [selectedAccounts, setSelectedAccounts] = useState(["145330"]);
  const [selectedAggregateAccounts, setSelectedAggregateAccounts] = useState([
    "250 ASSAY STREET, LLC",
    "PHMSCM Holdings",
  ]);
  const [selectedMeters, setSelectedMeters] = useState([
    "179071",
    "47125",
    "129120 (L)",
  ]);
  const [meterSearchQuery, setMeterSearchQuery] = useState("");

  // Color palette for multiple accounts
  const accountColors = [
    { line: "#00b8f1", gradient: "url(#lineGradient1)" },
    { line: "#4FC3F7", gradient: "url(#lineGradient2)" },
    { line: "#1E88E5", gradient: "url(#lineGradient3)" },
  ];

  // Map account IDs to consistent color indices
  const getColorIndex = (accountId) => {
    const colorMap = {
      145330: 0,
      "47197(H)": 1,
      "47197(L)": 2,
    };
    return colorMap[accountId] || 0;
  };

  const lineChartData = [
    { date: "Jan 04, 2026", 145330: 800, "47197(H)": 600, "47197(L)": 400 },
    {
      date: "Jan 11, 2026",
      145330: 3200,
      "47197(H)": 2800,
      "47197(L)": 1900,
    },
    {
      date: "Jan 18, 2026",
      145330: 3850,
      "47197(H)": 3200,
      "47197(L)": 2600,
    },
    {
      date: "Jan 25, 2026",
      145330: 3100,
      "47197(H)": 2700,
      "47197(L)": 2200,
    },
    {
      date: "Feb 01, 2026",
      145330: 3950,
      "47197(H)": 3400,
      "47197(L)": 2900,
    },
    {
      date: "Feb 08, 2026",
      145330: 4100,
      "47197(H)": 3600,
      "47197(L)": 3100,
    },
  ];

  const dailyBarChartData = [
    { day: "Monday", 145330: 450, "47197(H)": 380, "47197(L)": 320 },
    { day: "Tuesday", 145330: 320, "47197(H)": 280, "47197(L)": 240 },
    { day: "Wednesday", 145330: 520, "47197(H)": 440, "47197(L)": 370 },
    { day: "Thursday", 145330: 480, "47197(H)": 410, "47197(L)": 350 },
    { day: "Friday", 145330: 490, "47197(H)": 420, "47197(L)": 360 },
    { day: "Saturday", 145330: 380, "47197(H)": 320, "47197(L)": 270 },
    { day: "Sunday", 145330: 550, "47197(H)": 470, "47197(L)": 400 },
    { day: "Monday", 145330: 420, "47197(H)": 360, "47197(L)": 300 },
    { day: "Tuesday", 145330: 190, "47197(H)": 160, "47197(L)": 130 },
  ];

  const accounts = [
    {
      name: "PHMSCM Holdings",
      items: [
        {
          type: "Potable Domestic",
          id: "145330",
          dateRange: "Nov 2020 - Feb 2026",
        },
        {
          type: "Potable Domestic",
          id: "47197(H)",
          dateRange: "Feb 2026 - Feb 2026",
        },
        {
          type: "Potable Domestic",
          id: "47197(L)",
          dateRange: "Nov 2020 - Feb 2026",
        },
      ],
    },
  ];

  const toggleAccountSelection = (accountId) => {
    setSelectedAccounts((prev) =>
      prev.includes(accountId)
        ? prev.filter((id) => id !== accountId)
        : [...prev, accountId],
    );
  };

  const toggleAggregateAccountSelection = (accountName) => {
    setSelectedAggregateAccounts((prev) =>
      prev.includes(accountName)
        ? prev.filter((name) => name !== accountName)
        : [...prev, accountName],
    );
  };

  const toggleMeterSelection = (meterId) => {
    setSelectedMeters((prev) =>
      prev.includes(meterId)
        ? prev.filter((id) => id !== meterId)
        : [...prev, meterId],
    );
  };

  const filterMeters = (query) => {
    return accounts
      .flatMap((account) => account.items)
      .filter((item) => item.id.toLowerCase().includes(query.toLowerCase()))
      .map((item) => item.id);
  };

  // Aggregate Usage Data
  const aggregateAccounts = [
    { name: "250 ASSAY STREET, LLC" },
    { name: "PHMSCM Holdings" },
  ];

  const aggregateChartData = [
    {
      date: "Jan 04, 2026",
      "250 ASSAY STREET, LLC": 10000,
      "PHMSCM Holdings": 3000,
    },
    {
      date: "Jan 12, 2026",
      "250 ASSAY STREET, LLC": 13500,
      "PHMSCM Holdings": 4000,
    },
    {
      date: "Jan 19, 2026",
      "250 ASSAY STREET, LLC": 13200,
      "PHMSCM Holdings": 4200,
    },
    {
      date: "Jan 24, 2026",
      "250 ASSAY STREET, LLC": 12800,
      "PHMSCM Holdings": 3800,
    },
    {
      date: "Feb 02, 2026",
      "250 ASSAY STREET, LLC": 12500,
      "PHMSCM Holdings": 3500,
    },
  ];

  // Meters By Flow Data
  const metersData = {
    potableFire: [
      { name: "250 ASSAY STREET, LLC", id: "179071", flow: 242.1 },
      { name: "CVS Caremark #05617-01", id: "47125", flow: 2.15 },
      { name: "West Lake Village", id: "47159", flow: 0.1 },
      { name: "Summer Creek Baptist Chun", id: "47184", flow: 0 },
      { name: "Olive Garden #6413", id: "84753", flow: 0 },
      { name: "BROADBRIDGE LOGISTICS", id: "116083", flow: 0 },
      { name: "Courtyard by Marriott", id: "87224", flow: 0 },
      { name: "Humble", id: "47139", flow: 0 },
    ],
    nonPotable: [
      { name: "TechnipFMC", id: "129120 (L)", flow: 164603 },
      { name: "TechnipFMC", id: "129121", flow: 135564.4 },
      { name: "Generation Park", id: "129182", flow: 51178 },
      { name: "Generation Park", id: "47166", flow: 43610.8 },
    ],
  };

  const metersChartData = [
    { date: "Jan 06, 2026", 179071: 45000, 47125: 0, "129120 (L)": 60000 },
    { date: "Jan 12, 2026", 179071: 0, 47125: 0, "129120 (L)": 0 },
    { date: "Jan 19, 2026", 179071: 0, 47125: 0, "129120 (L)": 15000 },
    { date: "Jan 26, 2026", 179071: 0, 47125: 0, "129120 (L)": 10000 },
    { date: "Feb 01, 2026", 179071: 20000, 47125: 0, "129120 (L)": 0 },
  ];

  return (
    <>
      {/* Header */}
      <motion.div
        className="mb-6"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <h1
          className="text-4xl font-bold bg-clip-text text-transparent"
          style={{
            backgroundImage: `linear-gradient(to right, ${colors.primary}, ${colors.secondary})`,
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            lineHeight: "normal",
          }}
        >
          Water Usage
        </h1>
        <p className="text-sm mt-1" style={{ color: colors.textSecondary }}>
          Monitor and analyze water consumption patterns
        </p>
      </motion.div>

      {/* Tab Controls */}
      <motion.div
        className="flex gap-3 mb-6 flex-wrap"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1 }}
      >
        {/* Water Usage Tab */}
        <button
          onClick={() => setActiveTab("waterUsage")}
          className="max-[567px]:w-full flex items-center gap-2 px-4 py-2.5 rounded-lg border transition-all duration-300 relative overflow-hidden group"
          style={{
            background:
              activeTab === "waterUsage"
                ? `${colors.primary}20`
                : colors.cardBg,
            borderColor:
              activeTab === "waterUsage" ? colors.primary : colors.border,
          }}
        >
          {activeTab === "waterUsage" && (
            <motion.div
              className="absolute inset-0"
              style={{
                background: `linear-gradient(to right, transparent, ${colors.primary}30, transparent)`,
              }}
              animate={{ x: ["-100%", "200%"] }}
              transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
            />
          )}
          <Droplets
            className="w-4 h-4 relative z-10"
            style={{
              color:
                activeTab === "waterUsage"
                  ? colors.primary
                  : colors.textSecondary,
            }}
          />
          <span
            className="text-sm font-semibold relative z-10"
            style={{
              color:
                activeTab === "waterUsage" ? colors.text : colors.textSecondary,
            }}
          >
            Water Usage
          </span>
          <InfoTooltip text="Shows water consumption per meter for the selected date range. Compare usage of up to 8 meters at once." />
        </button>

        {/* Aggregate Usage Tab */}
        <button
          onClick={() => setActiveTab("aggregateUsage")}
          className="max-[567px]:w-full flex items-center gap-2 px-4 py-2.5 rounded-lg border transition-all duration-300 relative overflow-hidden group"
          style={{
            background:
              activeTab === "aggregateUsage"
                ? `${colors.primary}20`
                : colors.cardBg,
            borderColor:
              activeTab === "aggregateUsage" ? colors.primary : colors.border,
            boxShadow:
              activeTab === "aggregateUsage"
                ? `0 0 20px ${colors.primary}40`
                : "none",
          }}
        >
          {activeTab === "aggregateUsage" && (
            <motion.div
              className="absolute inset-0"
              style={{
                background: `linear-gradient(to right, transparent, ${colors.primary}30, transparent)`,
              }}
              animate={{ x: ["-100%", "200%"] }}
              transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
            />
          )}
          <TrendingUp
            className="w-4 h-4 relative z-10"
            style={{
              color:
                activeTab === "aggregateUsage"
                  ? colors.primary
                  : colors.textSecondary,
            }}
          />
          <span
            className="text-sm font-semibold relative z-10"
            style={{
              color:
                activeTab === "aggregateUsage"
                  ? colors.text
                  : colors.textSecondary,
            }}
          >
            Aggregate Usage
          </span>
          <InfoTooltip text="Shows total water consumption per account for the selected date range. Use the category toggle to view usage by meter category within each account." />
        </button>

        {/* Meters By Flow Tab */}
        <button
          onClick={() => setActiveTab("metersByFlow")}
          className="max-[567px]:w-full flex items-center gap-2 px-4 py-2.5 rounded-lg border transition-all duration-300 relative overflow-hidden group"
          style={{
            background:
              activeTab === "metersByFlow"
                ? `${colors.primary}20`
                : colors.cardBg,
            borderColor:
              activeTab === "metersByFlow" ? colors.primary : colors.border,
            boxShadow:
              activeTab === "metersByFlow"
                ? `0 0 20px ${colors.primary}40`
                : "none",
          }}
        >
          {activeTab === "metersByFlow" && (
            <motion.div
              className="absolute inset-0"
              style={{
                background: `linear-gradient(to right, transparent, ${colors.primary}30, transparent)`,
              }}
              animate={{ x: ["-100%", "200%"] }}
              transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
            />
          )}
          <BarChart3
            className="w-4 h-4 relative z-10"
            style={{
              color:
                activeTab === "metersByFlow"
                  ? colors.primary
                  : colors.textSecondary,
            }}
          />

          <span
            className="text-sm font-semibold relative z-10"
            style={{
              color:
                activeTab === "metersByFlow"
                  ? colors.text
                  : colors.textSecondary,
            }}
          >
            Meters By Flow
          </span>

          <InfoTooltip text="Lists meters in descending order of total flow for the selected date range and preselected meter categories. View water consumption trends over the selected period." />
        </button>
      </motion.div>

      {/* Tabs Content (moved to components) */}
      {activeTab === "waterUsage" && (
        <WaterUsageTab
          accounts={accounts}
          selectedAccounts={selectedAccounts}
          toggleAccountSelection={toggleAccountSelection}
          viewType={viewType}
          setViewType={setViewType}
          lineChartData={lineChartData}
          dailyBarChartData={dailyBarChartData}
          accountColors={accountColors}
          getColorIndex={getColorIndex}
        />
      )}

      {activeTab === "aggregateUsage" && (
        <AggregateUsageTab
          aggregateAccounts={aggregateAccounts}
          selectedAggregateAccounts={selectedAggregateAccounts}
          toggleAggregateAccountSelection={toggleAggregateAccountSelection}
          aggregateChartData={aggregateChartData}
        />
      )}

      {activeTab === "metersByFlow" && (
        <MetersByFlowTab
          metersData={metersData}
          metersChartData={metersChartData}
          selectedMeters={selectedMeters}
          toggleMeterSelection={toggleMeterSelection}
          meterSearchQuery={meterSearchQuery}
          setMeterSearchQuery={setMeterSearchQuery}
          filterMeters={filterMeters}
        />
      )}
    </>
  );
}

export default WaterUsageDetail;